// ============ INDEXEDDB CHUNK CACHE ============
// Terrain edits (world_deltas/{addr}/chunks/*) cached locally so revisiting a world paints instantly and costs
// ZERO Firestore reads.
//
// WHY THIS EXISTS, and why it shipped alongside guest mode rather than after it: a signed-out visitor now gets an
// anonymous uid, and an anonymous uid can READ. Without a cache, every casual visitor opens a live onSnapshot on a
// whole chunks collection and holds it — which is precisely the read fan-out the cost work spent this session
// removing. Guest mode without this would have reintroduced the bill it was meant to avoid.
//
// The guest/member split is the point:
//   GUEST  — cannot edit, so has no use for live updates. Cache paint, then ONE getDocs. No listener, ever.
//   MEMBER — needs peers' edits as they happen, so keeps onSnapshot; the cache only removes the cold-start flash.
//
// Deliberately NOT a correctness store. It is a read accelerator: every entry is re-validated by the fetch that
// follows it, a miss costs only the normal read, and a stale entry is overwritten seconds later. So it is safe to
// wipe, safe to fail, and safe to ignore — every call below swallows its own errors and degrades to "no cache",
// because a browser in private mode (or with storage disabled) must still be able to play.

// A NEW NAME FORGETS EVERY DEVICE'S CACHE AT ONCE (Oct 6: the owner wiped the builds on their worlds and moved them to the
// newest terrain; the old name would have painted the wiped builds for up to a fortnight). dropChunks below is what keeps
// a later wipe from needing this again.
const DB_NAME = 'kubicor-chunks-2';
const STORE = 'chunks';
const DB_VERSION = 1;
// Entries older than this are ignored on read. A world you last saw a fortnight ago has probably moved on, and
// re-reading it once is cheaper than showing terrain that is visibly wrong.
const MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;

let _dbp = null;
function openDb() {
  if (_dbp) return _dbp;
  _dbp = new Promise((resolve) => {
    let req;
    try { req = indexedDB.open(DB_NAME, DB_VERSION); }
    catch (e) { resolve(null); return; }                       // private mode / storage blocked → no cache, no error
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);   // key = "<sanitizedAddr>/<chunkId>"
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
    req.onblocked = () => resolve(null);
  });
  return _dbp;
}

function tx(db, mode) {
  try { return db.transaction(STORE, mode).objectStore(STORE); } catch (e) { return null; }
}

// Every chunk cached for one world. Resolves to { chunkId: blocks } — empty when there is nothing (or no cache).
export async function readWorld(sanitized) {
  const db = await openDb(); if (!db) return {};
  const store = tx(db, 'readonly'); if (!store) return {};
  return await new Promise((resolve) => {
    const out = {}, prefix = sanitized + '/', cutoff = Date.now() - MAX_AGE_MS;
    let req;
    // A key range beats iterating the whole store: one world's chunks are contiguous under its prefix, so this
    // stays O(that world) no matter how many worlds have been visited.
    try { req = store.openCursor(IDBKeyRange.bound(prefix, prefix + '￿')); }
    catch (e) { resolve({}); return; }
    req.onsuccess = () => {
      const cur = req.result;
      if (!cur) { resolve(out); return; }
      const v = cur.value;
      if (v && v.blocks && v.cachedAt > cutoff) out[String(cur.key).slice(prefix.length)] = v.blocks;
      cur.continue();
    };
    req.onerror = () => resolve(out);
  });
}

// Forget chunks the server no longer has (a wiped or reset world): the first live snapshot names every chunk the world
// holds, so any cached one it does not is gone. Fire-and-forget, like the rest.
export async function dropChunks(sanitized, ids) {
  if (!ids || !ids.length) return;
  const db = await openDb(); if (!db) return;
  const store = tx(db, 'readwrite'); if (!store) return;
  for (const id of ids) { try { store.delete(sanitized + '/' + id); } catch (e) {} }
}

// Upsert chunks for one world. `chunks` is { chunkId: blocks }. Fire-and-forget by design — a failed write just
// means the next visit pays a read.
export async function writeWorld(sanitized, chunks) {
  const db = await openDb(); if (!db) return;
  const store = tx(db, 'readwrite'); if (!store) return;
  const now = Date.now();
  try { for (const id in chunks) store.put({ blocks: chunks[id], cachedAt: now }, sanitized + '/' + id); }
  catch (e) { /* quota or a closed tx — the cache is optional, so this is not worth surfacing */ }
}

export async function clearAll() {
  const db = await openDb(); if (!db) return;
  const store = tx(db, 'readwrite'); if (store) { try { store.clear(); } catch (e) {} }
}
